import { DURATION, EASE, SCENE_DURATION, SPRING, toMs } from "@/lib/motion"
import { animate, useReducedMotionConfig, type AnimationPlaybackControls } from "motion/react"
import { useEffect, useRef, useState } from "react"

/** Where the day-to-night scene is: daylight, the sunset crossfade, then full night (moon, stars, lit windows). */
export type ScenePhase = "day" | "dusk" | "night"

/** Props of a seasonal scene; `onPhaseChange` reports each phase so the page can restyle and start its celebration at nightfall. */
export type SeasonalSceneProps = { onPhaseChange: (phase: ScenePhase) => void }

type Star = { left: string; top: string; size: "sm" | "md" | "lg"; delay: string }

const STAR_COUNT = 34
const HORIZON_SINK = 0.97
const CLOUD = "M6 38C2 38 0 34 4 31C2 24 10 19 18 22C20 12 34 8 42 15C48 4 66 4 72 14C80 8 94 10 96 20C106 18 116 24 114 32C118 36 114 38 110 38Z"
const CLOUD_BANK =
  "M0 80V44Q20 28 46 36Q64 16 92 28Q112 10 140 26Q166 14 186 30Q210 12 238 28Q262 16 284 32Q310 14 336 30Q362 20 380 34Q392 30 400 34V80Z"
const MANOR = [
  "M92 240V98H148V240Z",
  "M84 102L120 26L156 102Z",
  "M118.5 4V28H121.5V4ZM112 11H128V14H112Z",
  "M40 240V150H92V240Z",
  "M32 154L66 100L100 154Z",
  "M64.5 80V102H67.5V80ZM59 86H74V89H59Z",
  "M148 240V140H204V240Z",
  "M140 144L176 86L212 144Z",
  "M174.5 66V88H177.5V66ZM169 72H184V75H169Z",
  "M204 196H228V200H204ZM204 176H228V179H204ZM207 179H209V196H207ZM213 179H215V196H213ZM219 179H221V196H219ZM225 179H227V196H225Z",
  "M68 132H92V136H68ZM68 114H92V117H68ZM70 117H72V132H70ZM76 117H78V132H76ZM82 117H84V132H82Z",
].join("")
const TREE = [
  "M0 360V300C18 260 30 220 26 170C22 130 10 100 0 80V40C24 70 44 110 50 160C56 220 46 290 60 360Z",
  "M40 180C80 160 120 150 170 120L174 128C128 158 88 172 46 196Z",
  "M120 150C140 130 150 110 152 84L158 86C158 114 148 138 128 158Z",
  "M150 128L210 112L156 136Z",
  "M100 162L130 196L94 170Z",
  "M30 120C60 96 84 70 100 36L106 40C92 76 68 104 38 132Z",
  "M80 80L124 60L84 88Z",
  "M96 46L104 6L102 48Z",
  "M48 240C80 232 104 220 126 200L130 206C108 228 84 242 50 252Z",
  "M104 216L150 214L108 224Z",
  "M20 90C14 60 18 30 28 4L33 6C24 32 22 60 28 88Z",
].join("")

function arch(x: number, y: number, w: number, h: number): string {
  const r = w / 2
  return `M${x} ${y + h}V${y + r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h}Z`
}

const WINDOWS = [
  arch(113, 116, 14, 22),
  arch(99, 158, 12, 20),
  arch(129, 158, 12, 20),
  arch(59, 176, 14, 22),
  arch(160, 166, 12, 20),
  arch(181, 166, 12, 20),
  arch(106, 198, 28, 42),
].join("")
const DOOR = arch(109, 201, 22, 39)

function seededRandom(seed: number): () => number {
  let state = seed
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function makeStars(): Star[] {
  const random = seededRandom(31)
  const sizes: Star["size"][] = ["sm", "sm", "md", "md", "lg"]
  return Array.from({ length: STAR_COUNT }, (_, index) => {
    const edge = index % 3 === 0
    const x = edge ? (random() < 0.5 ? 1 + random() * 4 : 95 + random() * 4) : 2 + random() * 96
    const y = edge ? 30 + random() * 26 : 2 + random() * 26
    return {
      left: `${x.toFixed(1)}%`,
      top: `${y.toFixed(1)}%`,
      size: sizes[Math.floor(random() * sizes.length)],
      delay: `${(random() * 1.6).toFixed(2)}s`,
    }
  })
}

function makeGrass(): string {
  const random = seededRandom(7)
  const base = (x: number) => 30 + Math.sin((x / 400) * Math.PI * 2) * 2
  let d = `M0 70V${base(0).toFixed(1)}`
  for (let x = 0; x < GRASS_WIDTH; x += 4) {
    const tip = base(x) - 5 - random() * 13
    const lean = (random() - 0.5) * 6
    d += `L${x} ${base(x).toFixed(1)}L${(x + 2 + lean).toFixed(1)} ${tip.toFixed(1)}L${x + 4} ${base(x + 4).toFixed(1)}`
  }
  return `${d}V70H0Z`
}

const GRASS_WIDTH = 1600
const STARS = makeStars()
const GRASS = makeGrass()

function wait(seconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, toMs(seconds)))
}

function sinkDistance(scene: HTMLElement, orb: HTMLElement): number {
  const sceneBox = scene.getBoundingClientRect()
  return sceneBox.top + sceneBox.height * HORIZON_SINK - orb.getBoundingClientRect().top
}

/** Halloween order-confirmed backdrop: a day sky whose sun drops elastically out of sight while a full moon rises behind a haunted manor. */
export function HalloweenNightScene({ onPhaseChange }: SeasonalSceneProps) {
  const reduce = useReducedMotionConfig() ?? false
  const [phase, setPhase] = useState<ScenePhase>(reduce ? "night" : "day")
  const sceneRef = useRef<HTMLDivElement>(null)
  const sunRef = useRef<HTMLDivElement>(null)
  const moonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const scene = sceneRef.current
    const sun = sunRef.current
    const moon = moonRef.current
    if (!scene || !sun || !moon) return
    let cancelled = false
    const running: AnimationPlaybackControls[] = []
    const enter = (next: ScenePhase) => {
      if (cancelled) return
      setPhase(next)
      onPhaseChange(next)
    }

    if (reduce) {
      enter("night")
      return () => {
        cancelled = true
      }
    }

    const sunSink = sinkDistance(scene, sun)
    running.push(animate(moon, { y: sinkDistance(scene, moon) }, { duration: DURATION.none }))
    void (async () => {
      await wait(SCENE_DURATION.dayHold)
      if (cancelled) return
      const squash = animate(sun, { scaleX: 1.18, scaleY: 0.8, y: -16 }, { duration: SCENE_DURATION.squash, ease: EASE.standard })
      running.push(squash)
      await squash
      enter("dusk")
      running.push(animate(sun, { y: sunSink, scaleX: 0.9, scaleY: 1.14 }, SPRING.plunge))
      await wait(SCENE_DURATION.moonrise)
      if (cancelled) return
      running.push(animate(moon, { y: 0, scale: [0.82, 1] }, SPRING.rise))
      await wait(SCENE_DURATION.nightfall)
      enter("night")
    })()

    return () => {
      cancelled = true
      running.forEach((control) => control.stop())
    }
  }, [onPhaseChange, reduce])

  return (
    <div ref={sceneRef} className="ns" data-phase={phase} aria-hidden="true" data-testid="halloween-night-scene">
      <div className="ns-sky ns-sky-day" />
      <div className="ns-sky ns-sky-dusk" />
      <div className="ns-sky ns-sky-night" />

      {STARS.map((star, index) => (
        <span
          key={index}
          className={`ns-star ns-star-${star.size}`}
          style={{ left: star.left, top: star.top, animationDelay: star.delay, transitionDelay: star.delay }}
        />
      ))}

      <div ref={sunRef} className="ns-orb ns-sun">
        <div className="ns-sun-halo" />
        <svg viewBox="-50 -50 100 100">
          <circle r="48" className="ns-sun-disc" />
        </svg>
      </div>

      <div ref={moonRef} className="ns-orb ns-moon">
        <div className="ns-moon-halo" />
        <svg viewBox="-50 -50 100 100">
          <circle r="48" className="ns-moon-disc" />
          <path d="M-30-30Q-14-40 2-32Q12-24 0-18Q-14-14-24-20Q-34-24-30-30Z" className="ns-crater" />
          <path d="M14-36Q26-38 32-30Q34-24 24-24Q12-26 14-36Z" className="ns-crater" />
          <path d="M-8-6Q8-16 26-8Q38 0 30 12Q18 20 2 14Q-12 8-8-6Z" className="ns-crater" />
          <path d="M-40 4Q-30-4-22 4Q-20 14-30 16Q-40 14-40 4Z" className="ns-crater" />
          <path d="M-18 24Q-6 20 4 28Q6 36-6 38Q-18 36-18 24Z" className="ns-crater" />
          <path d="M28 26Q36 22 40 28Q38 36 30 34Q24 32 28 26Z" className="ns-crater" />
        </svg>
      </div>

      {[1, 2, 3].map((n) => (
        <svg key={n} viewBox="0 0 120 40" className={`ns-cloud ns-cloud-${n}`}>
          <path d={CLOUD} />
        </svg>
      ))}
      <svg viewBox="0 0 400 80" preserveAspectRatio="none" className="ns-cloudbank">
        <path d={CLOUD_BANK} />
      </svg>

      <svg viewBox="0 0 240 240" className="ns-manor">
        <path d={MANOR} className="ns-silhouette" />
        <path d={WINDOWS} className="ns-window" />
        <path d={DOOR} className="ns-silhouette" />
      </svg>
      <svg viewBox="0 0 220 360" className="ns-tree ns-tree-left">
        <path d={TREE} className="ns-silhouette" />
      </svg>
      <svg viewBox="0 0 220 360" className="ns-tree ns-tree-right">
        <path d={TREE} className="ns-silhouette" />
      </svg>
      <svg viewBox={`0 0 ${GRASS_WIDTH} 70`} preserveAspectRatio="xMidYMax slice" className="ns-ground">
        <path d={GRASS} className="ns-silhouette" />
      </svg>
    </div>
  )
}
